import { apiService } from '@/api'

/**
 * IP检测工具类
 * 负责检测用户IP并缓存到本地存储
 */
class IpCheckManager {
  constructor() {
    this.CACHE_KEY = 'user_ip_info'
    this.CACHE_TIMESTAMP_KEY = 'user_ip_timestamp'
    this.CACHE_DURATION = 5 * 60 * 1000 // 5分钟缓存时间
  }

  /**
   * 获取缓存的IP信息
   * @returns {Object|null} 缓存的IP信息或null
   */
  getCachedIpInfo() {
    try {
      const cachedData = localStorage.getItem(this.CACHE_KEY)
      const cachedTimestamp = localStorage.getItem(this.CACHE_TIMESTAMP_KEY)
      
      if (!cachedData || !cachedTimestamp) {
        return null
      }
      
      const timestamp = parseInt(cachedTimestamp, 10)
      const now = Date.now()
      
      // 检查缓存是否过期
      if (now - timestamp > this.CACHE_DURATION) {
        this.clearCache()
        return null
      }
      
      return JSON.parse(cachedData)
    } catch (error) {
      console.error('获取缓存IP信息失败:', error)
      this.clearCache()
      return null
    }
  }

  /**
   * 缓存IP信息到本地存储
   * @param {Object} ipInfo IP信息对象
   */
  cacheIpInfo(ipInfo) {
    try {
      localStorage.setItem(this.CACHE_KEY, JSON.stringify(ipInfo))
      localStorage.setItem(this.CACHE_TIMESTAMP_KEY, Date.now().toString())
    } catch (error) {
      console.error('缓存IP信息失败:', error)
    }
  }

  /**
   * 清除缓存的IP信息
   */
  clearCache() {
    try {
      localStorage.removeItem(this.CACHE_KEY)
      localStorage.removeItem(this.CACHE_TIMESTAMP_KEY)
    } catch (error) {
      console.error('清除IP缓存失败:', error)
    }
  }

  /**
   * 检测用户IP并缓存
   * @returns {Promise<Object>} IP检测结果
   */
  async checkAndCacheIp() {
    try {
      // 先尝试获取缓存
      // const cachedInfo = this.getCachedIpInfo()
      // if (cachedInfo) {
      //   return cachedInfo
      // }

      // 缓存不存在或已过期，请求API
      const response = await apiService.checkIpLogin()
      
      if (response && response.data && response.data.ip) {
        const ipInfo = {
          ip: response.data.ip,
          code: response.code,
          msg: response.msg,
          data: response.data,
          isSpecialUser: response.code === 1, // code=1表示特殊用户
          checkTime: Date.now()
        }
        
        // 缓存IP信息
        this.cacheIpInfo(ipInfo)
        
        return ipInfo
      } else {
        return null
      }
    } catch (error) {
      console.error('IP检测失败:', error)
      
      // 如果API请求失败，尝试返回缓存的信息（即使可能过期）
      const cachedInfo = this.getCachedIpInfo()
      if (cachedInfo) {
        return cachedInfo
      }
      
      return null
    }
  }

  /**
   * 强制刷新IP信息
   * @returns {Promise<Object>} IP检测结果
   */
  async forceRefreshIp() {
    this.clearCache()
    return await this.checkAndCacheIp()
  }

  /**
   * 获取当前IP地址
   * @returns {string|null} IP地址
   */
  getCurrentIp() {
    const cachedInfo = this.getCachedIpInfo()
    return cachedInfo ? cachedInfo.ip : null
  }

  /**
   * 检查是否为特殊用户
   * @returns {boolean} 是否为特殊用户
   */
  isSpecialUser() {
    const cachedInfo = this.getCachedIpInfo()
    return cachedInfo ? cachedInfo.isSpecialUser : false
  }

  /**
   * 获取IP检测消息
   * @returns {string|null} IP检测消息
   */
  getIpMessage() {
    const cachedInfo = this.getCachedIpInfo()
    return cachedInfo ? cachedInfo.msg : null
  }
}

// 创建单例实例
const ipCheckManager = new IpCheckManager()

export default ipCheckManager
